import { isString } from 'lodash';
import { type DataFrame, type Field, FieldType } from './data-frame';

export const MISSING_VALUE = undefined;

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * MutableDataFrame is a complex wrapper around the DataFrame interface
 *
 * @deprecated use standard DataFrame
 */
export class MutableDataFrame<T = any> implements DataFrame {
  fields: Field[] = [];

  private first: any[] = [];
  private parsers: Map<Field, (v: string) => any> = new Map();

  constructor() {
    // Get Length to show up if you use spread
    Object.defineProperty(this, 'length', {
      enumerable: true,
      get: () => {
        return this.first.length;
      },
    });
  }

  get length() {
    return this.first.length;
  }

  addField(f: Field): Field {
    const field: Field = {
      ...f,
    };

    this.fields.push(field);
    // The ?? case shouldn't ever happen since we just pushed a field, but make Typescript happy anyway
    this.first = this.fields[0]?.values ?? [];
    this.validate();

    return field;
  }

  validate() {
    // Make sure all arrays are the same length
    const length = this.fields.reduce((v: number, f) => {
      return Math.max(v, f.values.length);
    }, 0);

    // Add empty elements until everything matches
    for (const field of this.fields) {
      while (field.values.length !== length) {
        field.values.push(MISSING_VALUE);
      }
    }
  }

  /**
   * Add values from an object to corresponding fields. Similar to appendRow but does not create new fields.
   */
  add(value: T): void {
    // Will add one value for every field
    const obj = value as any;
    for (const field of this.fields) {
      let val = obj[field.name];

      if (field.type !== FieldType.string && isString(val)) {
        val = this.parseValue(field, val);
      }

      if (val === undefined) {
        val = MISSING_VALUE;
      }

      field.values.push(val);
    }
  }

  private parseValue(field: Field, value: string) {
    let parser = this.parsers.get(field);
    if (parser === undefined) {
      parser = makeFieldParser(value, field);
      this.parsers.set(field, parser);
    }
    return parser(value);
  }
}

function makeFieldParser(value: unknown, field: Field) {
  if (field.type === FieldType.number) {
    return (value: string) => {
      return parseFloat(value);
    };
  }

  // Will convert anything that starts with "T" to true
  if (field.type === FieldType.boolean) {
    return (value: string) => {
      return !(value[0] === 'F' || value[0] === 'f' || value[0] === '0');
    };
  }

  // Just pass the string back
  return (value: string) => value;
}
